/**
 * External
 */
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';

/**
 * Components
 */
import { format } from 'date-fns';
import DockButton from '../../dock/DockButton';
import DockAgeRating from '../../dock/dockRating/DockAgeRating';

/**
 * Styles
 */
import './PlayerDock.scss';

/**
 * Other
 */
import {
  goBackFromPlayer,
  getAssetNameWithQualifier,
  getGtmEventConfig,
} from '../../../utils';
import { history } from '../../../state/configureStore';
import { gtmDimensions } from '../../../components/GtmContext/dimensions/player';

function getNextAssetTitle({
  nextAsset,
}) {
  const { seasonNumber, number } = nextAsset.episode;

  return `T${seasonNumber} E${number} | ${nextAsset.title}`;
}

const getGenresText = (genres = []) => {
  let genresText = '';

  genres.forEach((item, index) => {
    if (index === genres.length - 1) {
      genresText = `${genresText}${item}`;
      return;
    }
    genresText = `${genresText}${item} • `;
  });

  return genresText;
};

/**
 * Component PlayerDock
 * @description Component to display content info on Player
 */
function PlayerDock({
  asset,
  intl: { formatMessage },
  className,
  // isLive,
  nextAsset,
  gtmInteraction,
  hasNextEpi,
}) {
  const epgRedirectionData = useSelector((state) => state.epgRedirectionData) || {};
  const {
    episode, live, title,
    rating, ratingWarning,
    genres,
  } = asset || {};
  const { showName } = episode || {};
  const { qualifiers } = live || {};
  const { title: nextTitle } = nextAsset || {};
  const { showName: nextShowName } = nextAsset?.episode || {};
  const { qualifiers: nextQualifiers, startTime } = nextAsset?.live || {};

  const formattedStartTime = startTime ? format(startTime, 'HH:mm') : '';

  const genresText = getGenresText(genres);

  const nextAssetTitle = nextAsset?.episode
    ? getNextAssetTitle({ nextAsset }) : getAssetNameWithQualifier(
      nextQualifiers,
      nextShowName || nextTitle,
      formatMessage,
    );

  const handleGtmEvent = (type) => {
    const { hash, dimensions, eventType } = getGtmEventConfig(
      asset.type,
      gtmDimensions,
      'player',
      type,
    );
    gtmInteraction(asset, hash, eventType, dimensions);
  };

  const goBack = () => {
    handleGtmEvent('close');
    goBackFromPlayer({ history, epgRedirectionData });
  };

  return (
    <div className={`dtv-dock ${className}`}>
      <DockButton
        className="dtv-dock-button-back dtv-icon-arrow-back"
        onClick={goBack}
        controlText={formatMessage({ id: 'player.back' })}
      />
      <div className="dtv-dock-left">
        <div className="dtv-dock-title">
          {getAssetNameWithQualifier(
            qualifiers,
            showName || title,
            formatMessage,
          )}
        </div>
        <DockAgeRating
          rating={rating}
          ratingDescription={genresText}
          ratingWarning={ratingWarning}
        />

      </div>
      {hasNextEpi && (
        <div className="dtv-dock-next-program-v2">
          {nextAssetTitle && (
          <div className="dtv-dock-next">
            {formatMessage({ id: nextAsset?.episode ? 'player.nextEpi' : 'player.next' })}
            <p className="dtv-dock-next-program-title" title={nextAssetTitle}>
              {formattedStartTime}
              {' '}
              {nextAssetTitle}
            </p>
          </div>
          )}
        </div>
      )}

    </div>
  );
}

PlayerDock.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  className: PropTypes.string,
  // isLive: PropTypes.bool,
  // isDelayed: PropTypes.bool,
  asset: PropTypes.shape({
    type: PropTypes.string,
  }),
  // channelLogo: PropTypes.arrayOf(PropTypes.shape({})),
  // channelName: PropTypes.string,
  nextAsset: PropTypes.shape({
    episode: PropTypes.shape({}),
    live: PropTypes.shape({}),
  }),
  gtmInteraction: PropTypes.func,
  hasNextEpi: PropTypes.bool,
};

PlayerDock.defaultProps = {
  className: '',
  isLive: false,
  isDelayed: false,
  asset: {},
  // channelLogo: [],
  // channelName: '',
  nextAsset: {
    name: '',
    showName: '',
  },
  gtmInteraction: () => {},
  hasNextEpi: false,
};

export default injectIntl(PlayerDock);
